import { Component, computed, input, output } from '@angular/core';
import * as fromSeriousSystem from '@serious-system';
import { teammateIconSections } from '../../teammates/teammate-icon-sections';
import * as fromAssistantIcon from '../assistants.icon.helper';

/**
 * The Skill Builder's icon panel — the same categorized grid the teammate picker
 * uses, drawn in its FILLED variant (SB-3519 T9, #1260).
 *
 * ## What this panel is, measured
 *
 * Figma `26752:166824` (inside the Skill Builder frame `25459:65782`) is
 * **376 × 364** with 8px padding and exactly one child: the icon list. No tabs,
 * no color row — those belong to the teammate picker, and this is where the two
 * hosts genuinely differ. So the scroller gets the whole remaining
 * `364 − 2 × 8 = 348px`, not the teammate picker's 198.
 *
 * ## Outline in, filled out
 *
 * The grid speaks OUTLINE names in both directions (KD-9). The stored value is
 * the labelled filled twin, `teammate-icons:<name>-filled` (KD-12). The two
 * conversions live in the helper as a stated pair, and this component binds
 * `selected` to the OUTLINE form — without that the panel would open with
 * nothing highlighted, silently, on every skill that already has an icon.
 *
 * ## It floats, and that was learned the hard way
 *
 * This component positions nothing: the Skill Builder mounts it in a
 * `cdkConnectedOverlay`, exactly as the teammate picker does. Two absolute
 * anchor positions were shipped to a browser first and both clipped **silently** — no
 * scrollbar, no error, just missing icon columns:
 *
 * | Anchoring | Measured 2026-09-02 |
 * | --- | --- |
 * | Centred on the trigger, per Figma | 148px outside the column; 4 of 6 icon columns |
 * | Right-anchored inside the column | fine at a 1441px window, left edge cut below ~1100px |
 *
 * The cause is not the anchor but the box: the form column carries
 * `tablet-landscape:overflow-x-hidden` and measures **336px at a 1024px window**,
 * narrower than this 376px panel — and it clips the bottom too. No inset fits a
 * box inside a smaller box. Figma shows the panel overlapping the sidebar because
 * a design frame is a flat canvas with nothing to clip against; the running app
 * has something.
 *
 * ⚠️ So do not "simplify" this back to `position: absolute`. It looks correct on
 * a wide screen and loses icons on a narrow one.
 *
 * Floating also gives **Escape-to-close for free**, which is why
 * [#1450](https://github.com/Insyders/squadbox-app/issues/1450) is closed by this
 * ticket rather than deferred.
 *
 * ## One thing it still refuses
 *
 * **Scrolling the selection into view.** `scrollIntoView` scrolls every
 * scrollable ancestor, and this panel opens with a value seeded every time, so
 * even inside an overlay it is a needless jolt on open. `scrollSelectedIntoView`
 * stays off.
 */
@Component({
  selector: 'squadbox-assistant-builder-logo-dropdown',
  standalone: true,
  imports: [fromSeriousSystem.IconCategoryGridComponent],
  template: `
    <sd-icon-category-grid
      [sections]="sections()"
      variant="filled"
      [selected]="selectedOutline()"
      [cursorSeed]="selectedOutline()"
      [scrollSelectedIntoView]="false"
      scrollerMaxHeightClass="max-h-[348px]"
      (picked)="handlePicked($event)"
    ></sd-icon-category-grid>
  `,
  styles: `
    @reference '../../../../styles.css';

    /* 376 x up-to-364, Figma 26752:166824. The grid and its scrolling belong to
       the organism; POSITIONING belongs to the CDK overlay the host mounts this
       in. So this component owns only the surface: size, background, padding.

       ⚠️ Do NOT put position:absolute and an inset back here. Both anchor positions were
       tried and measured in the browser 2026-09-02, and both clipped silently —
       the form column carries tablet-landscape:overflow-x-hidden and is 336px at
       a 1024px window, narrower than this 376px panel. Centred lost 148px off
       the right; right-anchored lost its left edge. There is no inset that fits
       a box wider than the box clipping it, which is why the panel floats. */
    :host {
      @apply block w-[376px] max-h-[364px];
      @apply bg-shades-white rounded-lg p-2 border border-neutral-200 shadow-lg;
    }
  `,
})
export class AssistantLogoDropdownComponent {
  /** The skill's stored `logo`. Any icon set, or a URL — all handled. */
  public readonly selected = input.required<string>();

  /** Emits the STORED value, so the form control can take it unchanged. */
  public readonly logoClicked = output<string>();

  /**
   * The library, grouped, with headings resolved by the APP — shared with the
   * teammate picker. This ticket adds no translation key.
   */
  protected readonly sections = teammateIconSections();

  /**
   * The stored value as an outline name, or `''` for anything this library does
   * not own — a legacy glyph, a URL, the schema default, or a stale labelled
   * value. `''` is right in every one of those cases: none of them is in this
   * grid, so nothing should read as chosen. Note the last one is why
   * `toOutlineSkillIcon` does not simply reuse `resolveSkillIconName`.
   */
  protected readonly selectedOutline = computed(() =>
    fromAssistantIcon.toOutlineSkillIcon(this.selected()),
  );

  protected handlePicked(
    outlineName: fromSeriousSystem.TeammateIconOutlineName,
  ): void {
    this.logoClicked.emit(fromAssistantIcon.toStoredSkillIcon(outlineName));
  }
}
